/**
 * Hook para monitoramento em tempo real
 * Gerencia detecções e alertas via WebSocket
 */

import { useState, useCallback } from 'react';
import { useWebSocket } from './useWebSocket';
import { 
  DetectionMessage, 
  AlertMessage, 
  CameraStatusMessage 
} from '../../data/services/WebSocketService';
import { buildWsUrl } from '../../config/environment';

interface RealtimeStats {
  totalDetections: number;
  totalPeople: number;
  averagePeople: number;
  lastUpdate: Date | null;
}

interface UseRealtimeMonitoringOptions {
  cameraId?: string;
  maxDetections?: number;
  maxAlerts?: number;
}

interface UseRealtimeMonitoringReturn {
  isConnected: boolean;
  detections: DetectionMessage[];
  alerts: AlertMessage[];
  cameraStatuses: Map<string, CameraStatusMessage>;
  stats: RealtimeStats;
  connect: () => void;
  disconnect: () => void;
  clearDetections: () => void;
  clearAlerts: () => void;
  acknowledgeAlert: (alertId: string) => void;
}

/**
 * Hook para monitoramento em tempo real de detecções e alertas
 * 
 * @param options - Opções de configuração
 * @returns Estado e funções do monitoramento em tempo real
 * 
 * @example
 * ```tsx
 * const { 
 *   isConnected, 
 *   detections, 
 *   alerts, 
 *   stats 
 * } = useRealtimeMonitoring({ maxDetections: 50 });
 * 
 * return (
 *   <div>
 *     <Badge variant={isConnected ? 'success' : 'error'}>
 *       {isConnected ? 'Conectado' : 'Desconectado'}
 *     </Badge>
 *     <p>Total de pessoas: {stats.totalPeople}</p>
 *     {alerts.map(alert => (
 *       <Alert key={alert.id} severity={alert.severity}>
 *         {alert.message}
 *       </Alert>
 *     ))}
 *   </div>
 * );
 * ```
 */
export function useRealtimeMonitoring(
  options: UseRealtimeMonitoringOptions = {}
): UseRealtimeMonitoringReturn {
  const { cameraId, maxDetections = 100, maxAlerts = 50 } = options;

  const [detections, setDetections] = useState<DetectionMessage[]>([]);
  const [alerts, setAlerts] = useState<AlertMessage[]>([]);
  const [cameraStatuses, setCameraStatuses] = useState<Map<string, CameraStatusMessage>>(
    new Map()
  );
  const [stats, setStats] = useState<RealtimeStats>({
    totalDetections: 0,
    totalPeople: 0,
    averagePeople: 0,
    lastUpdate: null,
  });

  // Construir URL do WebSocket
  const wsUrl = cameraId
    ? buildWsUrl(`/ws/camera/${cameraId}`)
    : buildWsUrl('/ws/live');

  // Handler de mensagens
  const handleMessage = useCallback((message: any) => {
    switch (message.type) {
      case 'detection':
        const detection = message.data as DetectionMessage;
        
        setDetections(prev => {
          const updated = [detection, ...prev];
          return updated.slice(0, maxDetections);
        });

        // Atualizar estatísticas
        setStats(prev => {
          const newTotal = prev.totalDetections + 1;
          const newPeople = prev.totalPeople + detection.person_count;
          return {
            totalDetections: newTotal,
            totalPeople: newPeople,
            averagePeople: newPeople / newTotal,
            lastUpdate: new Date(),
          };
        });
        break;

      case 'alert':
        const alert = message.data as AlertMessage;
        
        setAlerts(prev => {
          // Verificar se alerta já existe
          if (prev.some(a => a.id === alert.id)) {
            return prev;
          }
          const updated = [alert, ...prev];
          return updated.slice(0, maxAlerts);
        });
        break;

      case 'camera_status':
        const status = message.data as CameraStatusMessage;
        
        setCameraStatuses(prev => {
          const updated = new Map(prev);
          updated.set(status.camera_id, status);
          return updated;
        });
        break;
    }
  }, [maxDetections, maxAlerts]);

  // Usar WebSocket
  const { isConnected, connect, disconnect } = useWebSocket(wsUrl, {
    autoConnect: true,
    onMessage: handleMessage,
  });

  // Limpar detecções
  const clearDetections = useCallback(() => {
    setDetections([]);
    setStats({
      totalDetections: 0,
      totalPeople: 0,
      averagePeople: 0,
      lastUpdate: null,
    });
  }, []);

  // Limpar alertas
  const clearAlerts = useCallback(() => {
    setAlerts([]);
  }, []);

  // Reconhecer alerta
  const acknowledgeAlert = useCallback((alertId: string) => {
    setAlerts(prev => prev.filter(a => a.id !== alertId));
  }, []);

  return {
    isConnected,
    detections,
    alerts,
    cameraStatuses,
    stats,
    connect,
    disconnect,
    clearDetections,
    clearAlerts,
    acknowledgeAlert,
  };
}
